import {
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  createContext,
  useContext,
  type PropsWithChildren,
} from "react";
import { AccountSwitchOverlay } from "./AccountSwitchOverlay";
import { useNetworkState } from "react-use";
import { supabase } from "@/lib/supabase";
import {
  test_opfs,
  Weapon,
  type ListenerKey,
} from "../../../yap-frontend-rs/pkg/yap_frontend_rs";

export type WeaponToken = {
  browserSupported: true;
};

type WeaponState =
  | { type: "loading" }
  | { type: "error"; message: string }
  | {
      type: "ready";
      weapon: Weapon;
      userId: string | undefined;
      switching: boolean;
    };

const WeaponContext = createContext<WeaponState | undefined>(undefined);
// An account switch has two phases: creating the account's store (known
// here) and then rebuilding the course's deck from it (known only where the
// deck loads). Deck loaders report the second phase so one overlay covers both.
const DeckSwitchingContext = createContext<(switching: boolean) => void>(
  () => {},
);

const ORIGINAL_SESSION_KEY = "yap-impersonation-original-session";

function isImpersonating() {
  return !!localStorage.getItem(ORIGINAL_SESSION_KEY);
}

const SyncActionsContext = createContext<
  undefined | { syncNow: () => Promise<void>; forcePush: () => Promise<void> }
>(undefined);

export function WeaponProvider({
  userId,
  accessToken,
  children,
}: PropsWithChildren<{
  userId: string | undefined;
  accessToken: string | undefined;
}>) {
  const [loaded, setState] = useState<WeaponState>({ type: "loading" });
  // Derive this during render so no child can use the previous store with
  // the new account's credentials before the loading effect runs.
  const state = useMemo<WeaponState>(
    () =>
      loaded.type === "ready" && loaded.userId !== userId
        ? userId === undefined
          ? { type: "loading" }
          : { ...loaded, switching: true }
        : loaded,
    [loaded, userId],
  );
  const stateRef = useRef(state);
  const accessTokenRef = useRef<string | undefined>(accessToken);
  const networkState = useNetworkState();
  const networkStateRef = useRef(networkState);
  useLayoutEffect(() => {
    stateRef.current = state;
    accessTokenRef.current = accessToken;
    networkStateRef.current = networkState;
  }, [state, accessToken, networkState]);

  const sync = useCallback(
    async (listenerId: ListenerKey | undefined, streamId: string) => {
      const current = stateRef.current;
      if (current.type !== "ready" || current.switching) return;
      try {
        await current.weapon.sync(
          streamId,
          accessTokenRef.current,
          !!networkStateRef.current.online,
          listenerId,
          !isImpersonating(),
        );
      } catch (error) {
        console.warn("sync failed after store change", error);
      }
    },
    [],
  );

  useEffect(() => {
    const abortController = new AbortController();

    async function loadWeapon() {
      setState((previous) =>
        userId !== undefined && previous.type === "ready"
          ? { ...previous, switching: true }
          : { type: "loading" },
      );

      try {
        const weapon = await Weapon.create(
          userId,
          (listenerId: ListenerKey | undefined, streamId: string) => {
            if (!abortController.signal.aborted)
              return sync(listenerId, streamId);
          },
        );
        if (!abortController.signal.aborted) {
          setState({ type: "ready", weapon, userId, switching: false });
        }
      } catch (err: unknown) {
        if (!abortController.signal.aborted) {
          setState({
            type: "error",
            message: err instanceof Error ? err.message : String(err),
          });
        }
      }
    }

    loadWeapon();

    return () => abortController.abort();
  }, [userId, sync]);

  // Dev-only: expose the ready Weapon instance so E2E/screenshot tooling
  // (Playwright) can seed a deck deterministically without clicking through
  // onboarding. Never compiled into production builds.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (state.type !== "ready") return;
    (window as unknown as { __weapon?: Weapon }).__weapon = state.weapon;
    return () => {
      delete (window as unknown as { __weapon?: Weapon }).__weapon;
    };
  }, [state]);

  const syncWithSupabase = useCallback(async (forceUpload?: boolean) => {
    if (stateRef.current.type !== "ready" || stateRef.current.switching) return;
    if (accessTokenRef.current === undefined) return;
    try {
      if (networkStateRef.current.online) {
        const upload = forceUpload ?? !isImpersonating();
        await stateRef.current.weapon.sync_with_supabase(
          accessTokenRef.current,
          undefined,
          upload,
        );
      }
    } catch (e) {
      console.warn("sync_with_supabase failed", e);
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      void syncWithSupabase();
    }, 30_000);

    return () => {
      clearInterval(interval);
    };
  }, [syncWithSupabase]);

  // Initial stream sync may run before the auth token is available.
  useEffect(() => {
    if (!accessToken) return;
    if (state.type !== "ready") return;
    void syncWithSupabase();
  }, [accessToken, state, syncWithSupabase]);

  useEffect(() => {
    if (!window.BroadcastChannel) {
      console.log("BroadcastChannel not supported");
      return;
    }

    const channel = new BroadcastChannel("weapon-opfs-sync");

    channel.onmessage = (event) => {
      if (event.data?.type === "opfs-written" && event.data?.stream_id) {
        const streamId = event.data.stream_id;
        console.log(
          `Another tab wrote to OPFS for stream ${streamId}, reloading...`,
        );

        const currentState = stateRef.current;
        if (currentState.type === "ready") {
          currentState.weapon
            .load_from_local_storage(streamId)
            .then(() => {
              console.log(`Successfully reloaded stream ${streamId} from OPFS`);
            })
            .catch((e) => {
              console.warn(`Failed to reload stream ${streamId} from OPFS:`, e);
            });
        }
      }
    };

    return () => channel.close();
  }, []);

  useEffect(() => {
    if (!userId) return;
    if (!networkState.online) return;
    if (isImpersonating()) return;

    const channel = supabase
      .channel(`events:${userId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "events",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          try {
            const row = payload?.new;
            if (!row) return;
            const device_id: string = row.device_id;
            const stream_id: string = row.stream_id;
            const event_json: string = row.event;
            const stringified_event_json =
              typeof event_json !== "string"
                ? JSON.stringify(event_json)
                : event_json;

            const current = stateRef.current;
            if (
              current.type === "ready" &&
              !current.switching &&
              current.userId === userId &&
              device_id !== current.weapon.device_id
            ) {
              console.log(
                `Adding remote ${stream_id} event from ${device_id}`,
                stringified_event_json,
              );
              current.weapon.add_remote_event(
                device_id,
                stream_id,
                stringified_event_json,
              );
            }
          } catch (e) {
            console.error("Error handling realtime event", e);
          }
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [userId, networkState.online]);

  const actions = {
    syncNow: () => syncWithSupabase(),
    forcePush: () => syncWithSupabase(true),
  };

  const [deckSwitching, setDeckSwitching] = useState(false);
  return (
    <WeaponContext.Provider value={state}>
      <DeckSwitchingContext.Provider value={setDeckSwitching}>
        <SyncActionsContext.Provider value={actions}>
          {children}
        </SyncActionsContext.Provider>
      </DeckSwitchingContext.Provider>
      <AccountSwitchOverlay
        active={(state.type === "ready" && state.switching) || deckSwitching}
      />
    </WeaponContext.Provider>
  );
}

/** Keep the account-switch overlay up while this deck still shows the
 * previous account's data. */
export function useReportDeckSwitching(switching: boolean) {
  const setDeckSwitching = useContext(DeckSwitchingContext);
  useEffect(() => {
    setDeckSwitching(switching);
    return () => setDeckSwitching(false);
  }, [setDeckSwitching, switching]);
}

export function useWeapon(): Weapon {
  const ctx = useContext(WeaponContext);
  if (!ctx) throw new Error("useWeapon must be used within a WeaponProvider");
  if (ctx.type !== "ready") throw new Error("Weapon not ready");
  return ctx.weapon;
}

export function useWeaponState(): WeaponState {
  const ctx = useContext(WeaponContext);
  if (!ctx)
    throw new Error("useWeaponState must be used within a WeaponProvider");
  return ctx;
}

export function useSyncActions(): {
  syncNow: () => Promise<void>;
  forcePush: () => Promise<void>;
} {
  const ctx = useContext(SyncActionsContext);
  if (!ctx)
    throw new Error("useSyncActions must be used within a WeaponProvider");
  return ctx;
}

// Minimal WASM module using externref (reference types). Returns false on
// browsers that predate Chrome 86 / Firefox 79 where externref was behind a
// flag — those browsers can't compile our WASM module at all.
function supportsWasmExternref(): boolean {
  try {
    return WebAssembly.validate(
      new Uint8Array([
        0x00,
        0x61,
        0x73,
        0x6d,
        0x01,
        0x00,
        0x00,
        0x00, // magic + version
        0x01,
        0x05,
        0x01,
        0x60,
        0x00,
        0x01,
        0x6f, // type section: () -> externref
        0x03,
        0x02,
        0x01,
        0x00, // function section
        0x0a,
        0x06,
        0x01,
        0x04,
        0x00,
        0xd0,
        0x6f,
        0x0b, // code section
      ]),
    );
  } catch {
    return false;
  }
}

async function checkBrowserSupport(
  setBrowserSupported: (browserSupported: boolean) => void,
) {
  try {
    // Some browsers (e.g. certain locked-down WebViews) expose no
    // localStorage at all rather than throwing on access, so a bare read
    // can throw "Cannot read properties of null". That's just a cache miss,
    // not a reason to mark an otherwise-capable browser unsupported.
    let opfsTestPassed: string | null = null;
    try {
      opfsTestPassed = localStorage.getItem("opfs-test-passed");
    } catch (error) {
      console.warn("Could not read cached OPFS test result:", error);
    }

    // Reject browsers that don't support WebAssembly reference types
    // (externref). Our WASM module uses externref, so it can't compile on
    // Chrome < 86 / Firefox < 79 even if OPFS were somehow present.
    if (!supportsWasmExternref()) {
      setBrowserSupported(false);
      return;
    }

    // Quick JS-level check before calling into WASM (avoids noisy TypeErrors
    // on browsers that lack OPFS entirely or don't support createWritable)
    if (
      !navigator.storage ||
      typeof navigator.storage.getDirectory !== "function" ||
      typeof FileSystemFileHandle?.prototype?.createWritable !== "function"
    ) {
      setBrowserSupported(false);
      return;
    }

    if (opfsTestPassed === "true") {
      setBrowserSupported(true);
    } else {
      let timeoutId: number | undefined;
      const timeoutPromise = new Promise<boolean>((resolve) => {
        timeoutId = window.setTimeout(() => {
          console.log("OPFS test timed out after 3 seconds");
          resolve(false);
        }, 3000);
      });

      // Race between the OPFS test and the timeout
      const isSupported = await Promise.race([test_opfs(), timeoutPromise]);
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }

      setBrowserSupported(isSupported);

      if (isSupported) {
        // Store successful test result
        try {
          localStorage.setItem("opfs-test-passed", "true");
        } catch (error) {
          console.warn("Could not cache OPFS test result:", error);
        }
      }
    }
  } catch (error) {
    console.error("Browser support check failed:", error);
    // If test_opfs throws an error or times out, the browser is not supported
    setBrowserSupported(false);
  }
}

export function useWeaponSupport(): { browserSupported: true | false | null } {
  const [browserSupported, setBrowserSupported] = useState<boolean | null>(
    null,
  );
  useEffect(() => {
    checkBrowserSupport(setBrowserSupported);
  }, [setBrowserSupported]);

  return { browserSupported };
}
